import React from "react";
import Cards from "react-credit-cards-2";
import "react-credit-cards-2/es/styles-compiled.css";
import logoLumos from "../../assets/img/logo_lumos.png";
import {
  formatCreditCardNumber,
  formatCVC,
  formatExpirationDate
} from "../../assets/js/utils.js";
import {
  getBackendBaseUrl,
  getIntuitPaymentsBaseUrl
} from "../wizard/paymentsConfig";
import "./DirectChargePage.css";

class DirectChargePage extends React.Component {
  constructor(props) {
    super(props);

    // Read the GUID from the URL: /charge?id=<guid>
    const params = new URLSearchParams(window.location.search);
    const chargeId = params.get("id") || "";

    this.state = {
      chargeId,
      amount: null,
      description: "",
      isLoading: Boolean(chargeId),
      loadError: "",
      cardInformation: {
        name: "",
        number: "",
        expiry: "",
        cvc: "",
        postalCode: "",
        focused: ""
      },
      isPaying: false,
      paymentError: "",
      paymentSuccess: false
    };
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  componentDidMount() {
    // Same defensive approach as DataWizard — external scripts (HubSpot, etc.)
    // inject iframes and disable inputs. We fight back the same way.
    this.removeBlockingIframes();
    this.setupIframeObserver();

    setTimeout(() => {
      this.removeBlockingIframes();
      this.enableAllInputs();
      this.startInputKeepAlive();
    }, 100);

    setTimeout(() => {
      this.removeBlockingIframes();
      this.enableAllInputs();
    }, 500);

    if (this.state.chargeId) {
      this.fetchChargeInfo(this.state.chargeId);
    }
  }

  componentDidUpdate() {
    setTimeout(() => {
      this.enableAllInputs();
    }, 0);
  }

  componentWillUnmount() {
    if (this.iframeObserver) {
      this.iframeObserver.disconnect();
    }
    if (this.inputKeepAliveInterval) {
      clearInterval(this.inputKeepAliveInterval);
    }
  }

  // ─── Defensive helpers (ported from DataWizard) ──────────────────────────

  removeBlockingIframes = () => {
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
      const style = window.getComputedStyle(iframe);
      const rect = iframe.getBoundingClientRect();
      if (
        (rect.width > window.innerWidth * 0.9 &&
          rect.height > window.innerHeight * 0.9) ||
        parseInt(style.zIndex) > 90000
      ) {
        iframe.style.display = "none";
        iframe.remove();
      }
    });
    document.body.querySelectorAll("iframe").forEach((iframe) => {
      const style = window.getComputedStyle(iframe);
      if (parseInt(style.zIndex) > 90000) {
        iframe.remove();
      }
    });
  };

  setupIframeObserver = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === "IFRAME") {
            const style = window.getComputedStyle(node);
            if (parseInt(style.zIndex) > 90000) {
              node.remove();
            }
          } else if (node.querySelectorAll) {
            node.querySelectorAll("iframe").forEach((iframe) => {
              const style = window.getComputedStyle(iframe);
              if (parseInt(style.zIndex) > 90000) {
                iframe.remove();
              }
            });
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    this.iframeObserver = observer;
  };

  startInputKeepAlive = () => {
    if (this.inputKeepAliveInterval) {
      clearInterval(this.inputKeepAliveInterval);
    }
    this.inputKeepAliveInterval = setInterval(() => {
      const inputs = document.querySelectorAll(
        '.direct-charge-page input[type="text"]'
      );
      inputs.forEach((input) => {
        if (input.disabled) {
          input.disabled = false;
          input.removeAttribute("disabled");
        }
        input.style.pointerEvents = "auto";
        input.style.cursor = "text";
      });
    }, 100);
  };

  enableAllInputs = () => {
    const inputs = document.querySelectorAll(
      '.direct-charge-page input[type="text"]'
    );
    inputs.forEach((input) => {
      input.disabled = false;
      input.readOnly = false;
      input.removeAttribute("disabled");
      input.removeAttribute("readonly");
      input.style.pointerEvents = "auto";
      input.style.cursor = "text";
      input.style.zIndex = "999999";
      input.style.position = "relative";
      input.style.opacity = "1";

      const clickHandler = function (e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.focus();
      };
      const mouseDownHandler = function (e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      input.removeEventListener("click", clickHandler, true);
      input.removeEventListener("mousedown", mouseDownHandler, true);
      input.addEventListener("click", clickHandler, true);
      input.addEventListener("mousedown", mouseDownHandler, true);
    });

    const buttons = document.querySelectorAll(".direct-charge-page button");
    buttons.forEach((button) => {
      button.style.pointerEvents = "auto";
      button.style.zIndex = "999999";
      button.style.position = "relative";
    });
  };

  // ─── Charge info fetch ───────────────────────────────────────────────────

  fetchChargeInfo = async (chargeId) => {
    try {
      const url = getBackendBaseUrl();
      const response = await fetch(
        `${url}/api/payments/charge-info/${encodeURIComponent(chargeId)}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error("Invalid server response.");
      }

      if (!response.ok) {
        throw new Error(data?.message || "Could not load charge information.");
      }

      const amount = data?.amount ?? data?.data?.amount ?? null;
      const description = data?.description ?? data?.data?.description ?? "";

      if (amount === null) {
        throw new Error("Charge information is incomplete.");
      }

      this.setState({
        amount: parseFloat(amount).toFixed(2),
        description,
        isLoading: false
      });
    } catch (error) {
      this.setState({
        isLoading: false,
        loadError: error?.message || "Failed to load charge details."
      });
    }
  };

  // ─── Card helpers ────────────────────────────────────────────────────────

  getCardNumberDigits = () => {
    const value = this.state.cardInformation.number || "";
    return value.replace(/\s+/g, "").replace(/\D+/g, "");
  };

  parseExpiry = () => {
    const raw = this.state.cardInformation.expiry || "";
    const digits = raw.replace(/\D+/g, "");
    if (digits.length < 4) return null;
    const mm = digits.slice(0, 2);
    const yy = digits.slice(2, 4);
    const month = parseInt(mm, 10);
    const year2 = parseInt(yy, 10);
    if (!month || month < 1 || month > 12) return null;
    return { expMonth: mm, expYear2: yy, expYear4: String(2000 + year2) };
  };

  isCardFormValid = () => {
    const card = this.state.cardInformation;
    const name = (card.name || "").trim();
    const numberDigits = this.getCardNumberDigits();
    const expiry = this.parseExpiry();
    const cvc = (card.cvc || "").replace(/\D+/g, "");
    const postalCode = (card.postalCode || "").trim();

    if (name.length === 0) return false;
    if (numberDigits.length < 13 || numberDigits.length > 19) return false;
    if (!expiry) return false;
    if (cvc.length < 3) return false;
    if (postalCode.length < 3) return false;

    const now = new Date();
    const expYearNum = parseInt(expiry.expYear4, 10);
    const expMonthNum = parseInt(expiry.expMonth, 10);
    if (expYearNum < now.getFullYear()) return false;
    if (expYearNum === now.getFullYear() && expMonthNum < now.getMonth() + 1)
      return false;

    return true;
  };

  newRequestId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  // ─── Input handlers ──────────────────────────────────────────────────────

  handleCardInputChange = (e) => {
    const { name, value } = e.target;
    let valueResult = value;

    this.setState((prevState) => {
      const currentCardInfo = prevState.cardInformation;
      if (name === "number") {
        valueResult = formatCreditCardNumber(value);
      } else if (name === "expiry") {
        valueResult = formatExpirationDate(value);
      } else if (name === "cvc") {
        valueResult = formatCVC(value, currentCardInfo.cvc, {
          number: currentCardInfo.number
        });
      }
      return { cardInformation: { ...currentCardInfo, [name]: valueResult } };
    });
  };

  handleInputFocus = (e) => {
    this.setState((prevState) => ({
      cardInformation: { ...prevState.cardInformation, focused: e.target.name }
    }));
  };

  // ─── API calls ───────────────────────────────────────────────────────────

  getPaymentsAccessToken = async () => {
    const url = getBackendBaseUrl();
    const response = await fetch(`${url}/api/intuit/payments-access-token`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });
    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new Error("Invalid server response (token).");
    }
    const accessToken = data && (data.accessToken || data.data?.accessToken);
    if (!response.ok || !accessToken)
      throw new Error("Could not obtain payments access token.");
    return accessToken;
  };

  tokenizeCardWithIntuit = async (accessToken) => {
    const expiry = this.parseExpiry();
    if (!expiry) throw new Error("Invalid card expiry date.");

    const card = this.state.cardInformation;
    const requestId = this.newRequestId();
    const tokensUrl = `${getIntuitPaymentsBaseUrl()}/quickbooks/v4/payments/tokens`;

    const body = {
      card: {
        name: (card.name || "").trim(),
        number: this.getCardNumberDigits(),
        expMonth: expiry.expMonth,
        expYear: expiry.expYear4,
        cvc: (card.cvc || "").replace(/\D+/g, ""),
        address: { postalCode: (card.postalCode || "").trim(), country: "US" }
      }
    };

    const response = await fetch(tokensUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "Request-Id": requestId
      },
      body: JSON.stringify(body)
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new Error("Card tokenization failed (invalid response).");
    }
    const tokenValue = data && (data.value || data.tokenValue);
    if (!response.ok || !tokenValue) {
      throw new Error(
        data?.errors?.[0]?.message ||
          data?.message ||
          "Card tokenization failed."
      );
    }
    return tokenValue;
  };

  /**
   * Calls the EXISTING POST /api/payments/setup-card-on-file endpoint.
   * chargeNow: true  → cobra imediatamente
   * saveCard: true   → salva cartão para cobranças futuras (mesmo mecanismo do Step3)
   */
  setupCardOnFile = async (tokenValue) => {
    const url = getBackendBaseUrl();
    const payload = {
      tokenValue,
      chargeNow: true,
      saveCard: true,
      applicationContext: {
        applicationChoice: "Swift",
        partnerCode: null,
        partnerDiscountPercentage: 0
      },
      applicant: {
        chargeId: this.state.chargeId
      },
      amount: this.state.amount,
      currency: "USD"
    };

    const response = await fetch(`${url}/api/payments/setup-card-on-file`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new Error("Invalid server response (setup-card-on-file).");
    }
    const paymentIdForApply =
      data && (data.paymentIdForApply || data.data?.paymentIdForApply);
    if (!response.ok || !paymentIdForApply) {
      throw new Error(
        data?.messages?.map((m) => m).join("\n") ||
          data?.errors?.[0]?.message ||
          data?.message ||
          "Card charge and save failed."
      );
    }
    return paymentIdForApply;
  };

  /**
   * POST /api/payments/direct-charge
   * Apenas dá baixa no GUID — a cobrança e o save já foram feitos pelo setup-card-on-file.
   */
  confirmDirectCharge = async (paymentIdForApply) => {
    const url = getBackendBaseUrl();
    const payload = { chargeId: this.state.chargeId, paymentIdForApply };

    const response = await fetch(`${url}/api/payments/direct-charge`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    console.log("Confirming direct charge with payload:");
    console.log(payload);

    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new Error("Invalid server response (direct-charge confirmation).");
    }
    if (!response.ok) {
      throw new Error(
        data?.messages?.map((m) => m).join("\n") ||
          data?.errors?.[0]?.message ||
          data?.message ||
          "Failed to confirm charge. Please contact support."
      );
    }
    return data;
  };

  // ─── Main pay orchestration ──────────────────────────────────────────────

  pay = async (e) => {
    e.preventDefault();
    if (this.state.isPaying) return;

    if (!this.isCardFormValid()) {
      this.setState({
        paymentError:
          "Please fill in all card fields correctly before proceeding."
      });
      return;
    }

    this.setState({ isPaying: true, paymentError: "" });

    try {
      const accessToken = await this.getPaymentsAccessToken();
      console.log("Access token retrieved:");
      console.log(accessToken);

      const tokenValue = await this.tokenizeCardWithIntuit(accessToken);
      console.log("Card tokenized successfully:");
      console.log(tokenValue);

      const paymentIdForApply = await this.setupCardOnFile(tokenValue);
      console.log("Card setup on file completed, paymentIdForApply:");
      console.log(paymentIdForApply);

      await this.confirmDirectCharge(paymentIdForApply);

      this.setState({
        isPaying: false,
        paymentSuccess: true,
        cardInformation: {
          name: "",
          number: "",
          expiry: "",
          cvc: "",
          postalCode: "",
          focused: ""
        }
      });
    } catch (error) {
      this.setState({
        isPaying: false,
        paymentError:
          error?.message || "A server error occurred. Please try again."
      });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  render() {
    const {
      amount,
      description,
      chargeId,
      isLoading,
      loadError,
      cardInformation,
      isPaying,
      paymentError,
      paymentSuccess
    } = this.state;

    const canPay = this.isCardFormValid();

    // No GUID
    if (!chargeId) {
      return (
        <div
          className="direct-charge-page"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            pointerEvents: "auto",
            overflow: "auto"
          }}
        >
          <div className="direct-charge-card">
            <div className="direct-charge-logo-container">
              <img src={logoLumos} alt="Lumos" className="direct-charge-logo" />
            </div>
            <div className="direct-charge-error" style={{ marginTop: 24 }}>
              Invalid payment link. Please contact support.
            </div>
          </div>
        </div>
      );
    }

    // Loading
    if (isLoading) {
      return (
        <div
          className="direct-charge-page"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            pointerEvents: "auto",
            overflow: "auto"
          }}
        >
          <div className="direct-charge-card">
            <div className="direct-charge-logo-container">
              <img src={logoLumos} alt="Lumos" className="direct-charge-logo" />
            </div>
            <div className="direct-charge-header">
              <h1 className="direct-charge-title">Loading…</h1>
              <p className="direct-charge-subtitle">
                Retrieving your payment details.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Load error
    if (loadError) {
      return (
        <div
          className="direct-charge-page"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            pointerEvents: "auto",
            overflow: "auto"
          }}
        >
          <div className="direct-charge-card">
            <div className="direct-charge-logo-container">
              <img src={logoLumos} alt="Lumos" className="direct-charge-logo" />
            </div>
            <div className="direct-charge-error" style={{ marginTop: 24 }}>
              {loadError}
            </div>
          </div>
        </div>
      );
    }

    const inputStyle = {
      pointerEvents: "auto",
      cursor: "text",
      zIndex: 999999,
      position: "relative"
    };

    return (
      <div
        className="direct-charge-page"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483647,
          pointerEvents: "auto",
          overflow: "auto",
          isolation: "isolate"
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className="direct-charge-card"
          style={{
            pointerEvents: "auto",
            position: "relative",
            zIndex: 100000
          }}
        >
          {/* Logo */}
          <div className="direct-charge-logo-container">
            <img src={logoLumos} alt="Lumos" className="direct-charge-logo" />
          </div>

          {/* Header */}
          <div className="direct-charge-header">
            <h1 className="direct-charge-title">Secure Payment</h1>
            {description && (
              <p className="direct-charge-subtitle">{description}</p>
            )}
          </div>

          {/* Amount */}
          <div className="direct-charge-amount">${amount}</div>

          <hr className="direct-charge-divider" />

          {paymentSuccess ? (
            <div className="direct-charge-success">
              <h3>✅ Payment Successful!</h3>
              <p>Your payment of ${amount} has been processed. Thank you!</p>
            </div>
          ) : (
            <>
              {/* Card preview */}
              <div className="direct-charge-card-preview">
                <Cards
                  cvc={cardInformation.cvc}
                  expiry={cardInformation.expiry}
                  focused={cardInformation.focused}
                  name={cardInformation.name}
                  number={cardInformation.number}
                />
              </div>

              {/* Card fields */}
              <div className="direct-charge-fields">
                <div>
                  <label className="direct-charge-label">Cardholder Name</label>
                  <input
                    className="direct-charge-input"
                    type="text"
                    name="name"
                    placeholder="Full name on card"
                    value={cardInformation.name}
                    onChange={(e) => {
                      e.stopPropagation();
                      this.handleCardInputChange(e);
                    }}
                    onFocus={(e) => {
                      e.stopPropagation();
                      this.handleInputFocus(e);
                    }}
                    onChangeCapture={(e) => e.stopPropagation()}
                    onInputCapture={(e) => e.stopPropagation()}
                    disabled={isPaying}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="direct-charge-label">Card Number</label>
                  <input
                    className="direct-charge-input"
                    type="text"
                    name="number"
                    placeholder="0000 0000 0000 0000"
                    pattern="[\d| ]{16,22}"
                    value={cardInformation.number}
                    onChange={(e) => {
                      e.stopPropagation();
                      this.handleCardInputChange(e);
                    }}
                    onFocus={(e) => {
                      e.stopPropagation();
                      this.handleInputFocus(e);
                    }}
                    onChangeCapture={(e) => e.stopPropagation()}
                    onInputCapture={(e) => e.stopPropagation()}
                    disabled={isPaying}
                    style={inputStyle}
                  />
                </div>

                <div className="direct-charge-row">
                  <div>
                    <label className="direct-charge-label">Expiry</label>
                    <input
                      className="direct-charge-input"
                      type="text"
                      name="expiry"
                      placeholder="MM/YY"
                      pattern="\d\d/\d\d"
                      value={cardInformation.expiry}
                      onChange={(e) => {
                        e.stopPropagation();
                        this.handleCardInputChange(e);
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        this.handleInputFocus(e);
                      }}
                      onChangeCapture={(e) => e.stopPropagation()}
                      onInputCapture={(e) => e.stopPropagation()}
                      disabled={isPaying}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="direct-charge-label">CVC</label>
                    <input
                      className="direct-charge-input"
                      type="text"
                      name="cvc"
                      placeholder="CVC"
                      pattern="\d{3,4}"
                      value={cardInformation.cvc}
                      onChange={(e) => {
                        e.stopPropagation();
                        this.handleCardInputChange(e);
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        this.handleInputFocus(e);
                      }}
                      onChangeCapture={(e) => e.stopPropagation()}
                      onInputCapture={(e) => e.stopPropagation()}
                      disabled={isPaying}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="direct-charge-label">
                      ZIP / Postal Code
                    </label>
                    <input
                      className="direct-charge-input"
                      type="text"
                      name="postalCode"
                      placeholder="ZIP"
                      value={cardInformation.postalCode}
                      onChange={(e) => {
                        e.stopPropagation();
                        this.handleCardInputChange(e);
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        this.handleInputFocus(e);
                      }}
                      onChangeCapture={(e) => e.stopPropagation()}
                      onInputCapture={(e) => e.stopPropagation()}
                      disabled={isPaying}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {paymentError && paymentError.length > 0 && (
                <div className="direct-charge-error">{paymentError}</div>
              )}

              <div className="direct-charge-actions">
                <button
                  type="button"
                  className="direct-charge-btn-pay"
                  onClick={this.pay}
                  disabled={isPaying || !canPay}
                  style={{
                    pointerEvents: "auto",
                    position: "relative",
                    zIndex: 999999
                  }}
                >
                  {isPaying ? "Processing…" : `Pay $${amount}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default DirectChargePage;
